import { CommonModule, DOCUMENT, NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Type,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ModalService } from '@core/services/modal.service';
import { ModalConfig } from '@core/interfaces/modal.interface';

@Component({
    selector: 'app-modal',
    imports: [CommonModule, NgComponentOutlet],
    templateUrl: './modal.component.html',
    styleUrl: './modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent {
  readonly modalService = inject(ModalService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private previousBodyOverflow = '';
  private bodyScrollLocked = false;
  readonly isOpen$: Observable<boolean> = this.modalService.isOpen$;
  readonly config = toSignal(this.modalService.modalConfig$, {
    initialValue: null as ModalConfig | null,
  });
  readonly component = computed<Type<any> | null>(
    () => this.config()?.component ?? null,
  );
  readonly inputs = computed<{ data: any }>(() => ({
    data: this.config()?.data ?? null,
  }));

  constructor() {
    this.isOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen) => {
        if (isOpen) {
          this.lockBodyScroll();
        } else {
          this.unlockBodyScroll();
        }
      });
    this.destroyRef.onDestroy(() => this.unlockBodyScroll());
  }

  async close() {
    await this.modalService.close();
  }

  get modalTitle(): string {
    return this.config()?.title || '';
  }

  get modalSizeClasses(): string {
    const width = this.config()?.porpierties?.width || 'md';
    const height = this.config()?.porpierties?.height || '';
    const minWidth = this.config()?.porpierties?.minWidth || '';
    const minHeight = this.config()?.porpierties?.minHeight || '';

    const widthClass = {
      'sm': 'max-w-sm',
      'md': 'max-w-md',
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      'full': 'w-full'
    }[width] || 'max-w-md';

  const heightClass = {
  'sm': 'max-h-[30vh]',
  'md': 'max-h-[50vh]',
  'lg': 'max-h-[70vh]',
  'xl': 'max-h-[85vh]',
  '2xl': 'max-h-[95vh]',
  'full': 'max-h-[calc(100vh-3rem)]',
  'fluid': 'h-[90vh] sm:h-[80vh] md:h-[70vh] max-h-[calc(100vh-3rem)]',
  'fit': 'max-h-[calc(100vh-4rem)]',
}[height] || '';

    const minWidthClass = {
      'sm': 'min-w-sm',
      'md': 'min-w-md',
      'lg': 'min-w-lg',
      'xl': 'min-w-xl',
      '2xl': 'min-w-2xl',
      '3xl': 'min-w-3xl',
      '4xl': 'min-w-4xl',
      '5xl': 'min-w-5xl',
      '6xl': 'min-w-6xl',
      '7xl': 'min-w-7xl',
    }[minWidth] || '';

    const minHeightClass = {
      'sm': 'min-h-[30vh]',
      'md': 'min-h-[50vh]',
      'lg': 'min-h-[70vh]',
      'xl': 'min-h-[85vh]',
      '2xl': 'min-h-[95vh]',
      'full': 'min-h-full',
    }[minHeight] || '';

    return `${minWidthClass} ${widthClass} ${minHeightClass} ${heightClass}`.trim();
  }

  private lockBodyScroll(): void {
    if (this.bodyScrollLocked) return;
    this.previousBodyOverflow = this.document.body.style.overflow;
    this.document.body.style.overflow = 'hidden';
    this.bodyScrollLocked = true;
  }

  private unlockBodyScroll(): void {
    if (!this.bodyScrollLocked) return;
    this.document.body.style.overflow = this.previousBodyOverflow;
    this.bodyScrollLocked = false;
  }
}
